/**
 =========================================================
 * Material Dashboard 2 React - v2.1.0
 =========================================================

 * Product Page: https://www.creative-tim.com/product/material-dashboard-react
 * Copyright 2022 Creative Tim (https://www.creative-tim.com)

 Coded by www.creative-tim.com

 =========================================================

 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 */

/**
 This file is used for controlling the global states of the components,
 you can customize the states for the different components here.
 */

import {
  createContext,
  useContext,
  useReducer,
  useMemo,
  useState,
  useEffect,
} from 'react'

// prop-types is a library for typechecking of props
import PropTypes from 'prop-types'
import { useLocation, useNavigate } from 'react-router-dom'

import posthog from 'posthog-js'
import { jwtDecode } from 'jwt-decode'
import MeService from '../services/me-service'

// Material Dashboard 2 React main context
const MaterialUI = createContext()

// authentication context
export const AuthContext = createContext({
  isAuthenticated: false,
  login: () => {},
  register: () => {},
  logout: () => {},
})

const AuthContextProvider = ({ children }) => {
  const [userDetails, setUserDetails] = useState({
    isAdmin: false,
    isTeacher: false,
    registerType: null,
    wantInCommunity: true,
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  const getUserDetails = async () => {
    try {
      setIsLoading(true)
      const response = await MeService.me()
      setUserDetails({
        email: response.email,
        isAdmin: response.is_admin,
        isTeacher: response.is_teacher,
        registerType: response.register_type,
        name: response.name,
        hasDetails: response.hasDetails,
        faculties: response?.notification?.faculties,
        examTypes: response?.examTypes,
        tutorial: response?.tutorial,
        lastCommunityBannerTimestamp: response?.lastCommunityBannerTimestamp,
        wantInCommunity: response?.wantInCommunity,
      })
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  const updateUserDetails = () => {
    setUserDetails(prevUserDetails => ({
      ...prevUserDetails,
      hasDetails: true,
    }))
  }

  useEffect(() => {
    const token =
      localStorage.getItem('token') || sessionStorage.getItem('token')
    if (!token) return
    setIsAuthenticated(true)
    if (
      location.pathname === '/auth/login' ||
      location.pathname === '/auth/register'
    ) {
      navigate('/acasa')
    }
  }, [location.pathname, navigate])

  useEffect(() => {
    if (isAuthenticated) {
      getUserDetails()
    }
  }, [isAuthenticated])

  const login = (token, rememberMe) => {
    if (rememberMe) {
      localStorage.setItem('token', token)
    } else {
      sessionStorage.setItem('token', token)
    }

    localStorage.removeItem('grilaId')
    localStorage.removeItem('subjectId')
    localStorage.removeItem('solving_time_subject')
    localStorage.removeItem('tagFilters')
    localStorage.setItem('currentTourStep', '0')

    const decoded = jwtDecode(token)

    posthog.identify(`${decoded.id}#${decoded.email}`, {
      id: decoded.id,
      email: decoded.email,
    })
    setIsAuthenticated(true)
  }

  const logout = () => {
    localStorage.removeItem('token')
    sessionStorage.removeItem('token')
    localStorage.removeItem('grilaId')
    localStorage.removeItem('subjectId')
    localStorage.removeItem('solving_time_subject')
    localStorage.removeItem('currentTourStep')

    const keys = Object.keys(localStorage)
    for (const key of keys) {
      if (key.startsWith('solving_time')) {
        localStorage.removeItem(key)
      }
    }

    window.location = process.env.REACT_APP_URL
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        login,
        logout,
        userDetails,
        updateUserDetails,
        setUserDetails,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuthContext = () => useContext(AuthContext)

// Setting custom name for the context which is visible on react dev tools
MaterialUI.displayName = 'MaterialUIContext'

// Material Dashboard 2 React reducer
function reducer(state, action) {
  switch (action.type) {
    case 'MINI_SIDENAV': {
      return { ...state, miniSidenav: action.value }
    }
    case 'MINI_SIDENAV_BUTTON_ENABLED': {
      return { ...state, isMiniSidenavButtonEnabled: action.value }
    }
    case 'TRANSPARENT_SIDENAV': {
      return { ...state, transparentSidenav: action.value }
    }
    case 'WHITE_SIDENAV': {
      return { ...state, whiteSidenav: action.value }
    }
    case 'SIDENAV_COLOR': {
      return { ...state, sidenavColor: action.value }
    }
    case 'TRANSPARENT_NAVBAR': {
      return { ...state, transparentNavbar: action.value }
    }
    case 'FIXED_NAVBAR': {
      return { ...state, fixedNavbar: action.value }
    }
    case 'OPEN_CONFIGURATOR': {
      return { ...state, openConfigurator: action.value }
    }
    case 'DIRECTION': {
      return { ...state, direction: action.value }
    }
    case 'LAYOUT': {
      return { ...state, layout: action.value }
    }
    case 'DARKMODE': {
      return { ...state, darkMode: action.value }
    }
    case 'THEME': {
      return { ...state, theme: action.value }
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`)
    }
  }
}

// Material Dashboard 2 React context provider
function MaterialUIControllerProvider({ children }) {
  const initialState = {
    miniSidenav: true,
    isMiniSidenavButtonEnabled: true,
    transparentSidenav: false,
    whiteSidenav: false,
    sidenavColor: 'info',
    transparentNavbar: true,
    fixedNavbar: true,
    openConfigurator: false,
    direction: 'ltr',
    layout: 'dashboard',
    darkMode: false,
    theme: localStorage.getItem('theme') || 'dark',
  }

  const [controller, dispatch] = useReducer(reducer, initialState)

  const value = useMemo(() => [controller, dispatch], [controller, dispatch])

  return <MaterialUI.Provider value={value}>{children}</MaterialUI.Provider>
}

// Material Dashboard 2 React custom hook for using context
function useMaterialUIController() {
  const context = useContext(MaterialUI)

  if (!context) {
    throw new Error(
      'useMaterialUIController should be used inside the MaterialUIControllerProvider.',
    )
  }

  return context
}

// Typechecking props for the MaterialUIControllerProvider
MaterialUIControllerProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

// Context module functions
const setMiniSidenav = (dispatch, value) =>
  dispatch({ type: 'MINI_SIDENAV', value })
const setTheme = (dispatch, value) => dispatch({ type: 'THEME', value })
const setIsMiniSidenavButtonEnabled = (dispatch, value) =>
  dispatch({ type: 'MINI_SIDENAV_BUTTON_ENABLED', value })
const setTransparentSidenav = (dispatch, value) =>
  dispatch({ type: 'TRANSPARENT_SIDENAV', value })
const setWhiteSidenav = (dispatch, value) =>
  dispatch({ type: 'WHITE_SIDENAV', value })
const setSidenavColor = (dispatch, value) =>
  dispatch({ type: 'SIDENAV_COLOR', value })
const setTransparentNavbar = (dispatch, value) =>
  dispatch({ type: 'TRANSPARENT_NAVBAR', value })
const setFixedNavbar = (dispatch, value) =>
  dispatch({ type: 'FIXED_NAVBAR', value })
const setOpenConfigurator = (dispatch, value) =>
  dispatch({ type: 'OPEN_CONFIGURATOR', value })
const setDirection = (dispatch, value) => dispatch({ type: 'DIRECTION', value })
const setLayout = (dispatch, value) => dispatch({ type: 'LAYOUT', value })
const setDarkMode = (dispatch, value) => dispatch({ type: 'DARKMODE', value })

export {
  AuthContextProvider,
  MaterialUIControllerProvider,
  useMaterialUIController,
  setMiniSidenav,
  setIsMiniSidenavButtonEnabled,
  setTransparentSidenav,
  setWhiteSidenav,
  setSidenavColor,
  setTransparentNavbar,
  setFixedNavbar,
  setOpenConfigurator,
  setDirection,
  setLayout,
  setDarkMode,
  setTheme,
}
